import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { EuroDatePicker } from '@/components/ui/euro-date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, CalendarDays, CalendarRange } from 'lucide-react';
import { addDays, isoLocal, mondayOf } from '@/components/routeplans/routeplanWeek';
import { getISOWeek, getISOWeekYear, parseISO } from 'date-fns';

export type PeriodMode = 'week' | 'range';
export interface PeriodValue { mode: PeriodMode; weekStart: string; from: string; to: string }

// Delt periodevelger for Ukesplan (intern) og kundens bussplan-lenke (/kp/): «Velg uke» (standard) eller «Velg dato-intervall».
const toDate = (s: string) => parseISO(`${s}T00:00:00`);
export const weekLabel = (weekStart: string) => `${getISOWeekYear(toDate(weekStart))}-W${String(getISOWeek(toDate(weekStart))).padStart(2, '0')}`;
export const weekFromLabel = (label: string): string | null => {
  const m = /^(\d{4})-W(\d{1,2})$/i.exec(label || '');
  if (!m) return null;
  const jan4 = new Date(Number(m[1]), 0, 4);
  return isoLocal(addDays(mondayOf(jan4), (Number(m[2]) - 1) * 7));
};

export function quickRange(key: string, today = new Date()): { from: string; to: string } {
  const mon = mondayOf(today);
  switch (key) {
    case 'next_week': return { from: isoLocal(addDays(mon, 7)), to: isoLocal(addDays(mon, 13)) };
    case 'last_week': return { from: isoLocal(addDays(mon, -7)), to: isoLocal(addDays(mon, -1)) };
    case 'next_4_weeks': return { from: isoLocal(today), to: isoLocal(addDays(today, 27)) };
    case 'this_month': return { from: isoLocal(new Date(today.getFullYear(), today.getMonth(), 1)), to: isoLocal(new Date(today.getFullYear(), today.getMonth() + 1, 0)) };
    default: return { from: isoLocal(mon), to: isoLocal(addDays(mon, 6)) };
  }
}

interface Props {
  value: PeriodValue;
  onChange: (v: PeriodValue) => void;
  maxDays?: number;
  minDate?: string;
  maxDate?: string;
  testIdPrefix?: string;
}

export function PeriodPicker({ value, onChange, maxDays = 184, minDate, maxDate, testIdPrefix = 'period' }: Props) {
  const { t } = useTranslation();
  const QUICK_KEYS = ['this_week', 'next_week', 'next_4_weeks', 'this_month', 'last_week'];
  // Avledet: viser presetet som matcher valgt Fra/Til; manuell endring nullstiller automatisk.
  const quick = value.mode === 'range' ? (QUICK_KEYS.find((k) => { const r = quickRange(k); return r.from === value.from && r.to === value.to; }) || '') : '';
  const setMode = (mode: PeriodMode) => {
    if (mode === value.mode) return;
    if (mode === 'week') { const ws = isoLocal(mondayOf(toDate(value.from || value.weekStart))); onChange({ mode, weekStart: ws, from: ws, to: isoLocal(addDays(toDate(ws), 6)) }); }
    else onChange({ mode, weekStart: value.weekStart, from: value.weekStart, to: isoLocal(addDays(toDate(value.weekStart), 6)) });
  };
  const shiftWeek = (dir: number) => {
    const ws = isoLocal(addDays(toDate(value.weekStart), dir * 7));
    onChange({ mode: 'week', weekStart: ws, from: ws, to: isoLocal(addDays(toDate(ws), 6)) });
  };
  const setWeekFromDate = (d: string) => {
    if (!d) return;
    const ws = isoLocal(mondayOf(toDate(d)));
    onChange({ mode: 'week', weekStart: ws, from: ws, to: isoLocal(addDays(toDate(ws), 6)) });
  };
  const setRange = (from: string, to: string) => {
    if (!from || !to) return;
    let a = toDate(from), b = toDate(to);
    if (b < a) b = a;
    if ((b.getTime() - a.getTime()) / 86400000 + 1 > maxDays) b = addDays(a, maxDays - 1);
    onChange({ mode: 'range', weekStart: isoLocal(mondayOf(a)), from: isoLocal(a), to: isoLocal(b) });
  };
  const applyQuick = (key: string) => {
    const r = quickRange(key);
    setRange(r.from, r.to);
  };
  // Segmentert to-valgs-kontroll: hele etiketten på én linje, felles ramme, samme høyde (h-8) som resten av verktøylinjen.
  const seg = (mode: PeriodMode, Icon: any, label: string, first: boolean) => (
    <button type="button" onClick={() => setMode(mode)} data-testid={`${testIdPrefix}-mode-${mode}`} aria-pressed={value.mode === mode}
      className={`inline-flex items-center gap-1.5 px-3 h-8 text-xs font-medium whitespace-nowrap transition-colors ${first ? '' : 'border-l border-slate-200'} ${value.mode === mode ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" />{label}
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0" data-testid={`${testIdPrefix}-picker`} data-mode={value.mode}>
      <div className="inline-flex items-stretch shrink-0 rounded-md border border-slate-200 bg-white overflow-hidden" role="group" data-testid={`${testIdPrefix}-mode-toggle`}>
        {seg('week', CalendarDays, t('periodPicker.velgUke', 'Velg uke'), true)}
        {seg('range', CalendarRange, t('periodPicker.velgDatoIntervall', 'Velg dato-intervall'), false)}
      </div>
      {value.mode === 'week' ? (
        <div className="inline-flex items-center gap-1 shrink-0">
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => shiftWeek(-1)} data-testid={`${testIdPrefix}-prev-week`} aria-label={t('periodPicker.forrigeUke', 'Forrige uke')}><ChevronLeft className="w-4 h-4" /></Button>
          <EuroDatePicker value={value.weekStart} onChange={setWeekFromDate} min={minDate} max={maxDate} className="w-36 h-8 text-xs" data-testid={`${testIdPrefix}-week-picker`} />
          <span className="text-xs font-semibold text-slate-600 px-1 whitespace-nowrap" data-testid={`${testIdPrefix}-week-label`}>{t('periodPicker.uke', 'Uke')} {getISOWeek(toDate(value.weekStart))}</span>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => shiftWeek(1)} data-testid={`${testIdPrefix}-next-week`} aria-label={t('periodPicker.nesteUke', 'Neste uke')}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      ) : (
        <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-2">
          <div className="inline-flex items-center gap-1.5 shrink-0">
            <span className="text-xs text-slate-500 whitespace-nowrap">{t('periodPicker.fra', 'Fra')}</span>
            <EuroDatePicker value={value.from} onChange={(d: string) => setRange(d, value.to)} min={minDate} max={maxDate} className="w-36 h-8 text-xs" data-testid={`${testIdPrefix}-from`} />
          </div>
          <div className="inline-flex items-center gap-1.5 shrink-0">
            <span className="text-xs text-slate-500 whitespace-nowrap">{t('periodPicker.til', 'Til')}</span>
            <EuroDatePicker value={value.to} onChange={(d: string) => setRange(value.from, d)} min={value.from || minDate} max={maxDate} className="w-36 h-8 text-xs" data-testid={`${testIdPrefix}-to`} />
          </div>
          <Select value={quick} onValueChange={applyQuick}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid={`${testIdPrefix}-quick`}><SelectValue placeholder={t('periodPicker.hurtigvalg', 'Hurtigvalg…')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="this_week">{t('periodPicker.denneUken', 'Denne uken')}</SelectItem>
              <SelectItem value="next_week">{t('periodPicker.nesteUke', 'Neste uke')}</SelectItem>
              <SelectItem value="next_4_weeks">{t('periodPicker.neste4Uker', 'Neste 4 uker')}</SelectItem>
              <SelectItem value="this_month">{t('periodPicker.denneManeden', 'Denne måneden')}</SelectItem>
              <SelectItem value="last_week">{t('periodPicker.forrigeUke', 'Forrige uke')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
